'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import CloseSvg from '@/components/common/CloseSvg';

// Utility to merge inline styles, similar to cn() for classes
const mergeStyles = (...styles: (React.CSSProperties | undefined)[]): React.CSSProperties => {
    return Object.assign({}, ...styles.filter(Boolean));
};

const dialogStyles = `
  .dialog-content-fullscreen-mobile {
    inset: 0 !important;
    max-width: 100vw !important;
    max-height: calc(100vh - 30px) !important;
    translate: 0 0 !important;
    height: 100% !important;
    overflow-y: auto !important;
  }

  @media (min-width: 768px) {
    .dialog-content-fullscreen-mobile {
      inset: auto !important;
      left: 50% !important;
      top: 50% !important;
      max-width: 32rem !important;
      max-height: none !important;
      translate: -50% -50% !important;
      height: auto !important;
    }
  }

  .dialog-content-default {
    left: 50% !important;
    top: 50% !important;
    max-width: 90% !important;
    translate: -50% -50% !important;
  }

  @media (min-width: 640px) {
    .dialog-content-default {
      max-width: 32rem !important;
    }
  }

  .dialog-footer-responsive {
    display: flex !important;
    flex-direction: column-reverse !important;
  }

  @media (min-width: 640px) {
    .dialog-footer-responsive {
      flex-direction: row !important;
      justify-content: flex-end !important;
      gap: 0.5rem !important;
    }
  }
`;

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ style, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            ...style,
        }}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    fullscreenOnMobile?: boolean;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
    ({ style, children, fullscreenOnMobile = false, className = '', ...props }, ref) => {
        const responsiveClass = fullscreenOnMobile ? 'dialog-content-fullscreen-mobile' : 'dialog-content-default';

        return (
            <DialogPortal>
                <style>{dialogStyles}</style>
                <DialogOverlay />
                <DialogPrimitive.Content
                    ref={ref}
                    className={`${responsiveClass} ${className}`}
                    style={{
                        position: 'fixed',
                        zIndex: 50,
                        display: 'grid',
                        width: '100%',
                        gap: '1rem',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#ffffff',
                        padding: '1.5rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        ...style,
                    }}
                    {...props}
                >
                    {children as React.ReactNode}
                    <DialogPrimitive.Close
                        style={{
                            position: 'absolute',
                            right: '1rem',
                            top: '1rem',
                            borderRadius: '0.375rem',
                            opacity: 0.7,
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'opacity 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.opacity = '0.7';
                        }}
                    >
                        <CloseSvg style={{ width: '1rem', height: '1rem' }} />
                        <span style={{ display: 'none' }}>Close</span>
                    </DialogPrimitive.Close>
                </DialogPrimitive.Content>
            </DialogPortal>
        );
    },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ style, ...props }, ref) => (
        <div
            ref={ref}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.375rem',
                textAlign: 'left',
                ...style,
            }}
            {...props}
        />
    ),
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ style, className = '', ...props }, ref) => (
        <div ref={ref} className={`dialog-footer-responsive ${className}`} style={mergeStyles(style)} {...props} />
    ),
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ style, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        style={{
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            ...style,
        }}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ style, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        style={{
            ...style,
        }}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
